/**
 * Entrar no catálogo do Mercado Livre (opt-in): quais anúncios tradicionais podem ganhar o anúncio de catálogo ligado a
 * eles, quais o ML vai exigir (aviso de catálogo obrigatório) e o opt-in em lote.
 */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw, Search } from 'lucide-react';
import { api, brl, dateTime, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Tag, Thumb } from '../components/ui.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';
import { can } from '@berytus/contracts';

interface Counts { total: number; ready: number; forewarning: number; eligible_tag: number; opted: number; inactive: number; not_eligible: number; unchecked: number; errors: number; pending: number; checkedAt: string | null }
interface Row {
  id: string; accountId: string; itemId: string; variationId: string | null; variation: string | null; title: string; sku: string | null; gtin: string | null;
  thumbnail: string | null; permalink: string | null; status: string; price: number | null; stock: number | null; catalogProductId: string | null;
  eligibility: string | null; reason: string | null; checkedAt: string | null; eligibleTag: boolean; forewarning: boolean; opted: boolean;
  catalogItemId: string | null; pending: boolean; error: string | null; optinAt: string | null;
}
interface Page { accounts: { id: string; name: string }[]; counts: Counts; total: number; page: number; pageSize: number; rows: Row[] }

const ELIG: Record<string, { label: string; tone: string; hint: string }> = {
  READY_FOR_OPTIN: { label: 'Pronto para entrar', tone: 'ok', hint: 'Pode ganhar o anúncio de catálogo.' },
  ALREADY_OPTED_IN: { label: 'Já no catálogo', tone: 'info', hint: 'Já tem o anúncio de catálogo ligado.' },
  PRODUCT_INACTIVE: { label: 'Sem ficha ativa', tone: 'off', hint: 'O ML ainda não ligou o anúncio a uma ficha de catálogo ativa (confira EAN e ficha técnica).' },
  NOT_ELIGIBLE: { label: 'Não elegível', tone: 'off', hint: 'Uma regra do ML impede (ex.: anúncio inativo, produto usado).' },
  CLOSED: { label: 'Finalizado', tone: 'off', hint: 'O anúncio não pode mais vender.' },
  COMPETING: { label: 'Competindo', tone: 'info', hint: 'Já está competindo no catálogo.' },
};

export function CatalogOptinPage() {
  const me = useMe();
  const admin = can(me.tenant.access, 'catalog_optin', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const page = Number(params.get('page') ?? 1);
  const filter: Record<string, string> = {};
  for (const k of ['account', 'state', 'q']) if (params.get(k)) filter[k] = params.get(k)!;
  const qs = new URLSearchParams({ ...filter, page: String(page), pageSize: '100' });
  const data = useQuery({
    queryKey: ['catalog-optin', qs.toString()],
    queryFn: () => api<Page>(`/channels/mercadolivre/catalog-optin?${qs}`),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => ((q.state.data?.counts.pending ?? 0) > 0 ? 5000 : false),
  });
  const d = data.data;
  const accounts = d?.accounts ?? [];
  const account = params.get('account') ? accounts.find((a) => a.id === params.get('account')) ?? null : accounts.length === 1 ? accounts[0]! : null;
  const c = d?.counts;

  const scan = useMutation({
    mutationFn: () => api('/channels/mercadolivre/catalog-optin/scan', { method: 'POST', json: { accountId: account!.id } }),
    onSuccess: () => setNotice('Consulta iniciada no Mercado Livre: elegíveis, avisos de catálogo obrigatório e a situação de cada um. Em alguns minutos os números aparecem aqui (atualize a página).'),
  });
  const optin = useMutation({
    mutationFn: (listingIds?: string[]) => api<{ queued: number }>('/channels/mercadolivre/catalog-optin', {
      method: 'POST', json: { accountId: account!.id, listingIds, filter: listingIds ? undefined : filter },
    }),
    onSuccess: (r) => {
      setPicked(new Set());
      setNotice(r.queued ? `${int(r.queued)} anúncios enviados para o opt-in. A Berytus confere a elegibilidade de cada um antes; a tela atualiza sozinha e mostra o motivo dos recusados.` : 'Nenhum anúncio da seleção precisa de opt-in.');
      void qc.invalidateQueries({ queryKey: ['catalog-optin'] });
    },
  });
  const run = (ids?: string[]) => {
    const n = ids?.length ?? d?.total ?? 0;
    if (!window.confirm(`Entrar no catálogo: ${ids ? `${int(n)} anúncios escolhidos` : `todos os ${int(n)} do filtro`}. O Mercado Livre cria um anúncio de catálogo ligado a cada um (mesmo estoque e preço). Continuar?`)) return;
    optin.mutate(ids);
  };

  const kpi = (label: string, value: number, note: string, state: string | null, tone?: 'alert') => {
    const on = (params.get('state') ?? null) === state;
    return (
      <button type="button" className={`kpi${on ? ' active' : ''}${tone === 'alert' && value > 0 ? ' alert' : ''}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
        onClick={() => set({ state: on ? null : state, page: null })}>
        <span className="k-label">{label}</span><span className="k-value">{int(value)}</span><span className="k-note">{note}</span>
      </button>
    );
  };
  const rows = d?.rows ?? [];
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.id));

  return (
    <>
      <Head title="Entrar no catálogo">
        <p>Anúncios tradicionais que podem ganhar o anúncio de catálogo (página de produto, disputa do buy box). O de catálogo fica ligado ao original e divide estoque e condições de venda. Os marcados com aviso o Mercado Livre vai exigir no catálogo: sem opt-in até a data, ele pausa o anúncio.</p>
      </Head>
      <div className="stack-y">
        {data.error ? <ErrorBox error={data.error} /> : d && c && (
          <>
            {accounts.length > 1 && (
              <div className="seg" role="group" aria-label="Conta">
                <button type="button" aria-pressed={!params.get('account')} onClick={() => set({ account: null, page: null })}>Todas as contas</button>
                {accounts.map((a) => <button key={a.id} type="button" aria-pressed={params.get('account') === a.id} onClick={() => set({ account: a.id, page: null })}>{a.name}</button>)}
              </div>
            )}
            <div className="kpis">
              {kpi('Prontos para entrar', c.ready, 'elegíveis e ainda sem catálogo', 'ready')}
              {kpi('Catálogo obrigatório', c.forewarning, 'aviso do ML: entrar antes da data', 'forewarning', 'alert')}
              {kpi('Já no catálogo', c.opted, 'têm o anúncio de catálogo', 'opted')}
              {kpi('Sem ficha ativa', c.inactive, 'o ML não achou a ficha', 'inactive')}
              {kpi('Não elegíveis', c.not_eligible, 'regra do ML impede', 'not_eligible')}
              {kpi('Não consultados', c.unchecked, `de ${int(c.total)} tradicionais`, 'unchecked')}
              {kpi('Recusados', c.errors, 'o último opt-in não passou', 'errors', 'alert')}
            </div>
            <div className="panel small" style={{ padding: '10px 14px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <span>Última consulta ao ML: {c.checkedAt ? dateTime(c.checkedAt) : <span className="muted">nunca</span>}</span>
              {c.pending > 0 && <span><RefreshCw size={12} /> {int(c.pending)} opt-ins em andamento…</span>}
              {admin && (account
                ? <button type="button" className="btn small" disabled={scan.isPending} onClick={() => scan.mutate()}><RefreshCw size={14} /> Consultar elegibilidade no ML</button>
                : <span className="small muted">Escolha a conta acima para consultar e fazer o opt-in.</span>)}
            </div>
            {notice && <div className="panel small" style={{ padding: '10px 14px' }}>{notice}</div>}
            {(scan.error || optin.error) && <ErrorBox error={scan.error ?? optin.error} />}

            <div className="toolbar">
              <label className="search">
                <Search size={16} />
                <span className="sr-only">Buscar</span>
                <input className="input" type="search" placeholder="Título, MLB, SKU ou EAN" value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <select className="select" aria-label="Situação" value={params.get('state') ?? 'all'} onChange={(e) => set({ state: e.target.value === 'all' ? null : e.target.value, page: null })}>
                <option value="all">Todos os tradicionais</option>
                <option value="ready">Prontos para entrar</option>
                <option value="forewarning">Catálogo obrigatório (aviso)</option>
                <option value="eligible_tag">Marcados como elegíveis pelo ML</option>
                <option value="opted">Já no catálogo</option>
                <option value="inactive">Sem ficha ativa</option>
                <option value="not_eligible">Não elegíveis</option>
                <option value="unchecked">Não consultados</option>
                <option value="errors">Opt-in recusado</option>
              </select>
              {admin && account && (
                <>
                  <span className="grow" />
                  <span className="small">{picked.size ? <b>{int(picked.size)} escolhidos</b> : <>Todos os <b>{int(d.total)}</b> do filtro</>}</span>
                  <button type="button" className="btn primary small" disabled={optin.isPending || (!picked.size && !d.total)} onClick={() => run(picked.size ? [...picked] : undefined)}>Entrar no catálogo</button>
                  {picked.size > 0 && <button type="button" className="btn small ghost" onClick={() => setPicked(new Set())}>Limpar escolha</button>}
                </>
              )}
            </div>

            <div className="panel flush">
              {!data.isPending && rows.length === 0 ? <div className="empty"><b>Nada por aqui</b>{c.total ? 'Nenhum anúncio com esse filtro.' : 'Nenhum anúncio tradicional ativo ou pausado.'}</div> : (
                <div className="grid-wrap" style={{ opacity: data.isPlaceholderData ? 0.55 : 1 }}>
                  <table className="grid" style={{ width: '100%', minWidth: 1050 }}>
                    <thead>
                      <tr>
                        {admin && account && <th style={{ width: 34 }}><input type="checkbox" className="check" aria-label="Escolher todos da página" checked={allPicked}
                          onChange={(e) => setPicked(e.target.checked ? new Set([...picked, ...rows.map((r) => r.id)]) : new Set([...picked].filter((id) => !rows.some((r) => r.id === id))))} /></th>}
                        <th>Anúncio tradicional</th><th style={{ width: 200 }}>Catálogo</th><th style={{ width: 130 }}>Ficha do ML</th>
                        <th style={{ width: 90 }} className="num">Preço</th><th style={{ width: 70 }} className="num">Estoque</th><th style={{ width: 280 }}>Opt-in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const e = r.opted ? ELIG.ALREADY_OPTED_IN! : r.eligibility ? ELIG[r.eligibility] : null;
                        return (
                          <tr key={r.id}>
                            {admin && account && <td><input type="checkbox" className="check" aria-label={`Escolher ${r.itemId}`} checked={picked.has(r.id)} disabled={r.opted}
                              onChange={(ev) => { const n = new Set(picked); if (ev.target.checked) n.add(r.id); else n.delete(r.id); setPicked(n); }} /></td>}
                            <td>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <Thumb src={r.thumbnail} alt="" />
                                <div className="cell-2" style={{ minWidth: 0 }}>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}{r.variation ? <span className="muted"> · {r.variation}</span> : null}</span>
                                  <span className="sub">
                                    {r.permalink ? <a href={r.permalink} target="_blank" rel="noreferrer" className="code">{r.itemId} <ExternalLink size={11} /></a> : <span className="code">{r.itemId}</span>}
                                    {r.sku ? ` · ${r.sku}` : ''}{r.status === 'paused' ? ' · pausado' : ''}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              {e ? <span title={e.hint}><Tag tone={e.tone}>{e.label}</Tag></span> : r.eligibleTag ? <Tag tone="ok">Elegível (marcado pelo ML)</Tag> : <span className="muted small">não consultado</span>}
                              {r.forewarning && !r.opted && <div><Tag tone="warn">Catálogo obrigatório</Tag></div>}
                              {r.reason && <div className="sub small muted" title={r.reason} style={{ whiteSpace: 'normal' }}>{r.reason.replaceAll('_', ' ')}</div>}
                            </td>
                            <td className="small">{r.catalogProductId ? <a href={`https://www.mercadolivre.com.br/p/${r.catalogProductId}`} target="_blank" rel="noreferrer" className="code">{r.catalogProductId} <ExternalLink size={11} /></a> : <span className="muted">—</span>}</td>
                            <td className="num">{brl(r.price)}</td>
                            <td className="num">{r.stock == null ? '—' : int(r.stock)}</td>
                            <td className="small">
                              {r.pending ? <span><RefreshCw size={12} /> em andamento…</span>
                                : r.error ? <div className="cell-2"><span style={{ color: 'var(--err)' }}>Recusado</span><span className="sub" title={r.error} style={{ whiteSpace: 'normal' }}>{r.error}</span></div>
                                : r.catalogItemId ? <span>Anúncio de catálogo <span className="code">{r.catalogItemId}</span>{r.optinAt ? <span className="muted"> · {dateTime(r.optinAt)}</span> : null}</span>
                                : <span className="muted">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
